import { attr, customElement, GenesisElement, observable } from '@genesislcap/web-core';
import { dealConditionsFormStyles as styles } from './deal-conditions-form.styles';
import { dealConditionsFormTemplate as template } from './deal-conditions-form.template';
import { MaturityOrder } from '../deal-series.types';
import { PriorityTypeEnum, RetailTypeEnum } from '../deal-series.types';
import {
  allowOnlyWholePositiveNumbers,
  applyRetailTypeConstraints,
  getMaximumOrderAmountValidation,
  getMinimumOrderAmountValidation,
  isOrderInTransit,
  normalizeNumericValueForStorage,
  orderAmountFormatter,
  resolveManualOrderQuantityEdit,
} from '../deal-series.util';
import { isOrderReconfirmed, getQuantityCellValidationState } from '../../deal-detail/deal-reconfirm.utils';
import { DealStatusEnum, TaxStatusEnum } from '../../deals.types';
import { isRepriceWindow } from '../deal-status';
import {
  normalizeNumericFieldForDisplay,
  NumericOrderAmountField,
} from './deal-conditions-form.util';

@customElement({
  name: 'deal-conditions-form',
  template,
  styles,
})
export class DealConditionsForm extends GenesisElement {
  
  @attr({ attribute: 'series-code' }) seriesCode: string;
  @attr({ attribute: 'series-size' }) seriesSize: string;

  @observable munisOrder: MaturityOrder;

  @attr({ attribute: 'tax-status' }) taxStatus: TaxStatusEnum;

  @attr({ attribute: 'security-type' }) securityType: string;

  @attr({ attribute: 'deal-status' }) dealStatus: string;

  /** When true, order amount field is read-only (split-order modal at par/principal cap). */
  @attr({ attribute: 'order-amount-locked', mode: 'boolean' }) orderAmountLocked = false;

  @observable flexMaturityActive: boolean = false;

  @observable flexMaturityOptions: MaturityOrder[] = [];

  flexMaturityField: HTMLElement;

  private handleDocumentClickBound: ((e: Event) => void) | null = null;

  /** Cached so Minimum field re-renders when munisOrder (incl. QUANTITY) is set from parent binding. */
  @observable minimumValidationState: { hasError: boolean; message: string } = { hasError: false, message: '' };

  munisOrderChanged(): void {
    this.refreshMinimumValidation();
  }

  private refreshMinimumValidation(): void {
    this.minimumValidationState = this.getMinimumValidation();
  }

  /** Push grid-edited quantity into the conditions form (split-order modal). */
  reflectExternalQuantity(order: MaturityOrder): void {
    // Reassigning munisOrder fires munisOrderChanged(), which refreshes validation.
    this.munisOrder = { ...order };
  }
  @observable editingNumericField: NumericOrderAmountField | null = null;

  handleNumericFieldFocus(field: NumericOrderAmountField, e: CustomEvent): void {
    this.editingNumericField = field;
    const input = (e.target as HTMLElement & { control?: HTMLInputElement })?.control
      ?? (e.target as HTMLInputElement);
    if (!input || typeof input.value !== 'string') {
      return;
    }
    const raw = this.munisOrder?.[field];
    input.value =
      field === 'QUANTITY'
        ? normalizeNumericFieldForDisplay(raw, this.munisOrder)
        : normalizeNumericFieldForDisplay(raw);
  }

  handleNumericFieldBlur(e: CustomEvent): void {
    const field = this.editingNumericField;
    const input = (e.target as HTMLElement & { control?: HTMLInputElement })?.control
      ?? (e.target as HTMLInputElement);
    if (field && input && typeof input.value === 'string') {
      const digits = allowOnlyWholePositiveNumbers(input.value);
      input.value = digits === '' ? '' : orderAmountFormatter(digits);
    }
    this.editingNumericField = null;
  }

  getMinimumValidation(): { hasError: boolean; message: string } {
    return getMinimumOrderAmountValidation(this.munisOrder);
  }


  getQuantityValidation(): { hasError: boolean; hasWarning: boolean; message: string } {
    if (!this.munisOrder) {
      return { hasError: false, hasWarning: false, message: '' };
    }
    const state = getQuantityCellValidationState(this.munisOrder, this.dealStatus);
    return {
      hasError: state.hasError,
      hasWarning: state.hasWarning,
      message: state.tooltip ?? '',
    };
  }

  getMaximumValidation(): { hasError: boolean; message: string } {
    return getMaximumOrderAmountValidation(this.munisOrder);
  }

  get isConditionsDisabled(): boolean {
    if (!this.munisOrder) return true;
    const dealStatus = this.dealStatus?.trim?.();
    const inTransit = isOrderInTransit(this.munisOrder);
    const reconfirmed = isOrderReconfirmed(this.munisOrder);

    if (
      dealStatus === DealStatusEnum.PRELIMINARY_PRICING ||
      dealStatus === DealStatusEnum.RETAIL_ORDER_PERIOD
    ) {
      return inTransit;
    }

    if (isRepriceWindow(dealStatus)) {
      return inTransit || reconfirmed;
    }

    return reconfirmed || inTransit;
  }

  get isAllButtonActive(): boolean {
    return !!this.munisOrder && this.munisOrder.IS_AUTO_RESET_ORDER_AMOUNT;
  }

  private isOrderFieldValueUnchanged(property: keyof MaturityOrder, propertyValue: unknown): boolean {
    const current = this.munisOrder?.[property];
    if (
      property === 'QUANTITY' ||
      property === 'MINIMUM_ORDER_AMOUNT' ||
      property === 'MAXIMUM_ORDER_AMOUNT'
    ) {
      const order = property === 'QUANTITY' ? this.munisOrder : undefined;
      return (
        normalizeNumericValueForStorage(current as string | null | undefined, order) ===
        normalizeNumericValueForStorage(propertyValue as string | null | undefined, order)
      );
    }
    return current === propertyValue;
  }

  handleKeyup(e: CustomEvent, property: keyof MaturityOrder): void {
    let propertyValue;
    switch (property) {
      case 'MUNI_AON':
      case 'MUNI_STRIP': {
        propertyValue = (e.target as any).checked;
        if (propertyValue === !!this.munisOrder?.[property]) {
          return;
        }
        break;
      }

      case 'MAXIMUM_ORDER_AMOUNT':
      case 'MINIMUM_ORDER_AMOUNT':
        const inputValue = (e.target as any).value;
        const sanitizedValue = allowOnlyWholePositiveNumbers(String(inputValue));
        // Normalize: empty/zero → null (for storage), valid numbers → string value
        propertyValue = normalizeNumericValueForStorage(sanitizedValue);
        break;

      case 'QUANTITY': {
        const quantityInputValue = (e.target as any).value;
        const sanitizedQuantity = allowOnlyWholePositiveNumbers(String(quantityInputValue));
        const { quantity, isZeroCancelIntent, shouldIgnore } = resolveManualOrderQuantityEdit(
          this.munisOrder,
          sanitizedQuantity,
        );
        if (shouldIgnore) {
          return;
        }
        const quantityUnchanged = this.isOrderFieldValueUnchanged('QUANTITY', quantity);
        const intentUnchanged =
          (this.munisOrder?.IS_ZERO_CANCEL_INTENT === true) === isZeroCancelIntent;
        if (quantityUnchanged && intentUnchanged) {
          return;
        }
        this.munisOrder = {
          ...this.munisOrder,
          QUANTITY: quantity,
          IS_ZERO_CANCEL_INTENT: isZeroCancelIntent,
          IS_AUTO_RESET_ORDER_AMOUNT: false,
        };
        this.refreshMinimumValidation();
        this.$emit('orderChanged', this.munisOrder);
        return;
      }

      case 'FLEX_COUPON':
        const couponValue = (e.target as any).value;
        if (couponValue === '' || couponValue === null || couponValue === undefined) {
          propertyValue = null;
        } else {
          const numValue = parseFloat(couponValue);
          if (!isNaN(numValue)) {
            propertyValue = numValue.toFixed(3);
          } else {
            propertyValue = null;
          }
        }
        break;

      case 'ZIP_CODE':
      case 'COMMENT':
        const textValue = (e.target as any).value;
        // Normalize: empty string → null for cleared fields
        propertyValue = textValue === '' ? null : textValue;
        break;

      case 'RETAIL_TYPE':
        const retailValue = (e.target as any).value;
        propertyValue = retailValue === '' ? RetailTypeEnum.NONE : retailValue;
        break;

      case 'PRIORITY_TYPE':
        const priorityValue = (e.target as any).value;
        propertyValue = priorityValue === '' ? PriorityTypeEnum.NONE : priorityValue;
        break;

      default:
        propertyValue = (e.target as any).value;
    }

    let updatedOrder: MaturityOrder = {
      ...this.munisOrder,
      [property]: propertyValue,
    };

    if (property === 'ORDER_TYPE' || property === 'PRIORITY_TYPE') {
      updatedOrder = applyRetailTypeConstraints(updatedOrder);
    }

    if (this.isOrderFieldValueUnchanged(property, propertyValue)) {
      return;
    }

    this.munisOrder = updatedOrder;
    this.refreshMinimumValidation();

    this.$emit('orderChanged', this.munisOrder);
  }

  handleFlexMaturityChanged(e: CustomEvent): void {
    this.munisOrder = {
      ...this.munisOrder,
      FLEX_MATURITY: e.detail,
    };
    this.$emit('orderChanged', this.munisOrder);
  }

  handleStripChanged(e: CustomEvent): void {
    this.$emit('strip-changed', e.detail);
  }

  validateNumericInput(e: CustomEvent): void {
    const el: HTMLInputElement = e.target as HTMLInputElement;
    el.value = allowOnlyWholePositiveNumbers(el.value);
  }

  handleFlexMaturityFocus(): void {
    if (this.isConditionsDisabled) {
      return;
    }
    this.flexMaturityActive = true;
    if (!this.handleDocumentClickBound) {
      this.handleDocumentClickBound = this.handleDocumentClick.bind(this);
    }
    requestAnimationFrame(() => {
      document.addEventListener('click', this.handleDocumentClickBound, true);
    });
  }

  handleFlexMaturityClose(): void {
    this.flexMaturityActive = false;
    if (this.handleDocumentClickBound) {
      document.removeEventListener('click', this.handleDocumentClickBound, true);
    }
  }

  disconnectedCallback(): void {
    if (this.handleDocumentClickBound) {
      document.removeEventListener('click', this.handleDocumentClickBound, true);
    }
    super.disconnectedCallback();
  }

  private handleDocumentClick(e: Event): void {
    if (!this.flexMaturityActive || !this.flexMaturityField) {
      return;
    }
    const path = e.composedPath();
    if (!path.includes(this.flexMaturityField)) {
      this.handleFlexMaturityClose();
    }
  }
}
